import { useMemo, useState } from 'react';
import { updateMipKycRequired } from '@dealstack/frontend/shared/dataExchange/services/mip.service.ts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { KycSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/types.ts';
import {
	BankAccountApprovalMode,
	SummaryDealMip,
	UpdateMipKycSettingDto,
} from '@dealstack/shared/lib/generated';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { useMipKycSetting } from '@dealstack/frontend/app/pages/Project/MIP/hooks/useKycSetting.ts';
import { certificationToggleFollowing } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/KycSettings/KycSettings.utils.ts';
import { updateMipKycSetting } from '@dealstack/frontend/shared/dataExchange/services/kyc-setting.service.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';

interface Props {
	mipData: SummaryDealMip | undefined;
	mipId: string;
}

type SettingChanges = Partial<Record<keyof KycSettings, boolean>>;

const SETTING_ID_TO_DTO_FIELD: Record<
	Exclude<keyof KycSettings, 'kycRequired'>,
	keyof UpdateMipKycSettingDto
> = {
	proofOfIdentityRequired: 'isProofOfIdentityRequired',
	proofOfAddressRequired: 'isProofOfAddressRequired',
	proofOfInvestmentVehicleRequired: 'isProofOfInvestmentVehicleRequired',
	proofOfIdentityCertificationRequired: 'isProofOfIdentityCertificationRequired',
	proofOfAddressCertificationRequired: 'isProofOfAddressCertificationRequired',
	proofOfInvestmentVehicleCertificationRequired:
		'isProofOfInvestmentVehicleCertificationRequired',
	beneficialOwnershipRequired: 'isBeneficialOwnershipRequired',
	sourceOfFundsRequired: 'isSourceOfFundsRequired',
	pepRequired: 'isPepRequired',
};

/** The settings ids a saved DTO body touched, so the overlay releases exactly those switches. */
const settingIdsIn = (body: UpdateMipKycSettingDto): SettingChanges =>
	Object.fromEntries(
		Object.entries(SETTING_ID_TO_DTO_FIELD)
			.filter(([, field]) => field in body)
			.map(([id]) => [id, true])
	);

export const useKycSettings = ({ mipId }: Props) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();
	const [optimisticValues, setOptimisticValues] = useState<SettingChanges>({});

	/**
	 * Drops this save's keys, handing those switches back to the server's values. On success the
	 * refetch has already landed them — including any coercion the repository applied on write;
	 * on failure they are what reverts the switch. Left in place the overlay shadows the real
	 * setting for the rest of the page's life.
	 */
	const releaseOverlay = (changes: SettingChanges) =>
		setOptimisticValues((current) =>
			Object.fromEntries(Object.entries(current).filter(([key]) => !(key in changes)))
		);

	const saveFailed = (changes: SettingChanges) => {
		releaseOverlay(changes);
		showToastV2({
			state: 'error',
			title: tMipManage('kyc_settings.requirements.updated_error'),
		});
	};

	// On the mutation rather than per `mutate` call: in TanStack v5 a per-call callback fires only
	// for the latest call, so two quick toggles where the first fails left the first switch
	// showing a value that never persisted.
	const updateIKycRequiredRequest = useMutation({
		mutationFn: updateMipKycRequired,
		onSuccess: async () => {
			// The switch reads `isKycRequired` off the KYC setting, which this writes too, so both
			// queries have to land before the overlay lets go of the value.
			await Promise.all([
				queryClient.invalidateQueries({ queryKey: [API_PATHS.DEAL_DATA_GET] }),
				queryClient.invalidateQueries({ queryKey: [API_PATHS.KYC_SETTING_MIP_GET] }),
			]);
			releaseOverlay({ kycRequired: true });
			showToastV2({ title: tMipManage('kyc-settings.requirements.updated_success') });
		},
		onError: () => saveFailed({ kycRequired: true }),
	});
	const updateMipRequirementsRequest = useMutation({
		mutationFn: (data: UpdateMipKycSettingDto) => updateMipKycSetting(data),
		onSuccess: async (_result, body) => {
			await queryClient.invalidateQueries({ queryKey: [API_PATHS.KYC_SETTING_MIP_GET] });
			releaseOverlay(settingIdsIn(body));
			showToastV2({ title: tMipManage('kyc_settings.requirements.updated_success') });
		},
		onError: (_error, body) => saveFailed(settingIdsIn(body)),
	});

	/**
	 * Its own mutation rather than sharing the requirement toggles': handlers live on the mutation
	 * now, so sharing one would fire the requirements toast for this save as well, and this
	 * control's overlay is separate state that the shared release cannot reach.
	 */
	const updateBankAccountApprovalRequest = useMutation({
		mutationFn: (bankAccountApprovalMode: BankAccountApprovalMode) =>
			updateMipKycSetting({ bankAccountApprovalMode }),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: [API_PATHS.KYC_SETTING_MIP_GET] });
			setOptimisticApprovalMode(undefined);
			showToastV2({
				title: tMipManage('kyc_settings.bank_account_approval.updated_success'),
			});
		},
		onError: () => {
			setOptimisticApprovalMode(undefined);
			showToastV2({
				state: 'error',
				title: tMipManage('kyc_settings.requirements.updated_error'),
			});
		},
	});

	const updateCertificationTextRequest = useMutation({
		mutationFn: (certificationText: string | null) =>
			updateMipKycSetting({ certificationText }),
		onSuccess: () =>
			queryClient.invalidateQueries({ queryKey: [API_PATHS.KYC_SETTING_MIP_GET] }),
	});

	const { data: kycSetting, isLoading: isLoadingKycSetting } = useMipKycSetting(mipId);

	const { hasPermission } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: defaultToEmptyString(mipId),
		permissionRole: PermissionRole.EDITOR,
	});

	const [optimisticApprovalMode, setOptimisticApprovalMode] = useState<BankAccountApprovalMode>();

	// A programme that has never saved its KYC settings has no row, and its behaviour is the
	// schema defaults — the same values buildKycWorkflowSettingData falls back to. Showing
	// them is what a first save would write; rendering nothing left the page blank.
	const computedSettings = useMemo(() => {
		// Null only while the row is in flight, so the defaults below never flash over the
		// values that are about to arrive.
		if (isLoadingKycSetting) {
			return null;
		}
		if (!kycSetting) {
			return {
				proofOfIdentityRequired: true,
				proofOfAddressRequired: true,
				proofOfInvestmentVehicleRequired: true,
				kycRequired: true,
				// The three certification columns are the one part of the schema that defaults
				// off: a programme that has never saved its settings has not asked for any.
				proofOfIdentityCertificationRequired: false,
				proofOfAddressCertificationRequired: false,
				proofOfInvestmentVehicleCertificationRequired: false,
				beneficialOwnershipRequired: true,
				sourceOfFundsRequired: true,
				pepRequired: true,
			};
		}
		return {
			proofOfIdentityRequired: kycSetting.isProofOfIdentityRequired,
			proofOfAddressRequired: kycSetting.isProofOfAddressRequired,
			proofOfInvestmentVehicleRequired: kycSetting.isProofOfInvestmentVehicleRequired,
			kycRequired: kycSetting.isKycRequired,
			proofOfIdentityCertificationRequired: kycSetting.isProofOfIdentityCertificationRequired,
			proofOfAddressCertificationRequired: kycSetting.isProofOfAddressCertificationRequired,
			proofOfInvestmentVehicleCertificationRequired:
				kycSetting.isProofOfInvestmentVehicleCertificationRequired,
			beneficialOwnershipRequired: kycSetting.isBeneficialOwnershipRequired,
			sourceOfFundsRequired: kycSetting.isSourceOfFundsRequired,
			pepRequired: kycSetting.isPepRequired,
		};
	}, [kycSetting, isLoadingKycSetting]);

	const settings = useMemo<KycSettings | null>(() => {
		if (!computedSettings) {
			return null;
		}

		return {
			kycRequired: {
				id: 'kycRequired',
				checked: optimisticValues.kycRequired ?? computedSettings.kycRequired,
				disabled: false,
			},
			proofOfIdentityRequired: {
				id: 'proofOfIdentityRequired',
				checked:
					optimisticValues.proofOfIdentityRequired ??
					computedSettings.proofOfIdentityRequired,
				disabled: false,
			},
			proofOfAddressRequired: {
				id: 'proofOfAddressRequired',
				checked:
					optimisticValues.proofOfAddressRequired ??
					computedSettings.proofOfAddressRequired,
				disabled: false,
			},
			proofOfInvestmentVehicleRequired: {
				id: 'proofOfInvestmentVehicleRequired',
				checked:
					optimisticValues.proofOfInvestmentVehicleRequired ??
					computedSettings.proofOfInvestmentVehicleRequired,
				disabled: false,
			},
			proofOfIdentityCertificationRequired: {
				id: 'proofOfIdentityCertificationRequired',
				checked:
					optimisticValues.proofOfIdentityCertificationRequired ??
					computedSettings.proofOfIdentityCertificationRequired,
				disabled: false,
			},
			proofOfAddressCertificationRequired: {
				id: 'proofOfAddressCertificationRequired',
				checked:
					optimisticValues.proofOfAddressCertificationRequired ??
					computedSettings.proofOfAddressCertificationRequired,
				disabled: false,
			},
			proofOfInvestmentVehicleCertificationRequired: {
				id: 'proofOfInvestmentVehicleCertificationRequired',
				checked:
					optimisticValues.proofOfInvestmentVehicleCertificationRequired ??
					computedSettings.proofOfInvestmentVehicleCertificationRequired,
				disabled: false,
			},
			beneficialOwnershipRequired: {
				id: 'beneficialOwnershipRequired',
				checked:
					optimisticValues.beneficialOwnershipRequired ??
					computedSettings.beneficialOwnershipRequired,
				disabled: false,
			},
			sourceOfFundsRequired: {
				id: 'sourceOfFundsRequired',
				checked:
					optimisticValues.sourceOfFundsRequired ??
					computedSettings.sourceOfFundsRequired,
				disabled: false,
			},
			pepRequired: {
				id: 'pepRequired',
				checked: optimisticValues.pepRequired ?? computedSettings.pepRequired,
				disabled: false,
			},
		};
	}, [computedSettings, optimisticValues]);

	const toggleSetting = (id: keyof KycSettings) => {
		if (!mipId || !settings) {
			return;
		}

		const newValue = !settings[id].checked;
		const certification = certificationToggleFollowing(id, newValue);

		const changes: SettingChanges = {
			[id]: newValue,
			...(certification ? { [certification.id]: certification.value } : {}),
		};
		setOptimisticValues((prev) => ({ ...prev, ...changes }));

		if (id === 'kycRequired') {
			updateIKycRequiredRequest.mutate({ body: { isKycRequired: newValue } });
			return;
		}

		updateMipRequirementsRequest.mutate({
			[SETTING_ID_TO_DTO_FIELD[id]]: newValue,
			...(certification
				? { [SETTING_ID_TO_DTO_FIELD[certification.id]]: certification.value }
				: {}),
		});
	};

	const bankAccountApproval = {
		value:
			optimisticApprovalMode ??
			kycSetting?.bankAccountApprovalMode ??
			BankAccountApprovalMode.AUTO_APPROVE_ALL,
		disabled: updateBankAccountApprovalRequest.isPending,
	};

	const setBankAccountApproval = (value: BankAccountApprovalMode) => {
		setOptimisticApprovalMode(value);
		updateBankAccountApprovalRequest.mutate(value);
	};

	return {
		settings,
		toggleSetting,
		bankAccountApproval,
		setBankAccountApproval,
		hasPermission,
		certificationText: {
			savedText: kycSetting?.certificationText ?? null,
			save: updateCertificationTextRequest.mutateAsync,
			isSaving: updateCertificationTextRequest.isPending,
		},
		isLoading:
			updateIKycRequiredRequest.isPending ||
			updateMipRequirementsRequest.isPending ||
			isLoadingKycSetting,
	};
};
